// Adding colleagues (AddPeopleSheet.swift): the team's people (§19.1 — the first page at rest, a
// server search once something is typed), minus the people already here. For a CHANNEL the choice
// is added one membership at a time (§8.5.2: a partial success keeps who it added). For a GROUP the
// ids go to `channelGrowGroup`: a group's identity is its set of people, so "adding" opens the
// conversation of everyone here plus them, and this one stays as it is.
import { useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { userDisplayName } from '../../../core/channelListDerivation';
import type { ChannelRecord, UserRecord } from '../../../core/records';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Button, Dialog, InlineStatus, SearchInput } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';

const SEARCH_DEBOUNCE_MS = 250;

export function AddPeopleDialog({
  channel,
  teamId,
  existingIds,
  maxSelectable,
  onClose,
  onAdded,
  onChoose,
  title,
}: {
  channel: ChannelRecord;
  teamId: string;
  existingIds: ReadonlySet<string>;
  /** The group ceiling's free seats; null for a channel (no size rule). */
  maxSelectable: number | null;
  onClose(): void;
  onAdded(): void;
  /** Hand the chosen ids back instead of adding them (the «Solo lectura» posters list, §44.1). */
  onChoose?(ids: string[]): void;
  title?: string;
}) {
  const shell = useShell();
  const [term, setTerm] = useState('');
  const [candidates, setCandidates] = useState<UserRecord[] | null>(null);
  const [searchFailed, setSearchFailed] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const known = useRef(new Map<string, UserRecord>());
  const [busy, setBusy] = useState(false);
  const isGroup = channel.type === 'G';

  useEffect(() => {
    let alive = true;
    const id = setTimeout(
      () => {
        data.call('channelFindPeople', teamId, term).then(
          (users) => {
            if (!alive) return;
            for (const u of users) known.current.set(u.id, u);
            setCandidates(users);
            setSearchFailed(false);
          },
          () => alive && setSearchFailed(true),
        );
      },
      term ? SEARCH_DEBOUNCE_MS : 0,
    );
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [teamId, term]);

  const me = shell.me?.id ?? '';
  const rows = useMemo(
    () =>
      (candidates ?? [])
        .filter((u) => u.id !== me && u.deleteAt === 0)
        .map((u) => ({ user: u, name: userDisplayName(u), here: existingIds.has(u.id) }))
        .sort((a, b) => Number(a.here) - Number(b.here) || a.name.localeCompare(b.name)),
    [candidates, existingIds, me],
  );
  const full = maxSelectable !== null && selected.length >= maxSelectable;

  const toggle = (userId: string) =>
    setSelected((s) => (s.includes(userId) ? s.filter((x) => x !== userId) : full ? s : [...s, userId]));

  const submit = () => {
    if (!selected.length || busy) return;
    if (onChoose) {
      onChoose(selected);
      onClose();
      return;
    }
    setBusy(true);
    if (isGroup) {
      data.call('channelGrowGroup', channel.id, selected).then(
        (id) => {
          if (id) {
            onClose();
            shell.openChannel(id);
          } else {
            setBusy(false);
            shell.toast(t('people.openFailed'), 'error');
          }
        },
        () => {
          setBusy(false);
          shell.toast(t('people.openFailed'), 'error');
        },
      );
      return;
    }
    data.call('channelAddMembers', channel.id, selected).then(
      (added) => {
        if (added > 0) shell.toast(added === 1 ? t('web.channelPanel.addedOne') : tf('web.channelPanel.added', added));
        else shell.toast(t('web.channelPanel.addFailed'), 'error');
        onAdded();
        onClose();
      },
      () => {
        setBusy(false);
        shell.toast(t('web.channelPanel.addFailed'), 'error');
      },
    );
  };

  return (
    <Dialog
      title={title ?? (isGroup ? t('people.groupAdd') : t('people.addPeople'))}
      onClose={onClose}
      width={480}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button kind="primary" onClick={submit} disabled={!selected.length || busy}>
            {selected.length ? `${t('people.add')} (${selected.length})` : t('people.add')}
          </Button>
        </>
      }
    >
      <SearchInput value={term} onChange={setTerm} placeholder={t('people.search')} autoFocus />
      {selected.length ? (
        <div className="cp-chips">
          {selected.map((id) => {
            const u = known.current.get(id);
            const name = u ? userDisplayName(u) : id;
            return (
              <button key={id} className="cp-chip" onClick={() => toggle(id)} title={t('people.remove')}>
                {name}
                <Icon name="x" size={12} />
              </button>
            );
          })}
        </div>
      ) : null}
      {maxSelectable !== null ? <div className="cp-note">{tf('people.groupSeats', Math.max(0, maxSelectable - selected.length))}</div> : null}
      {searchFailed ? <InlineStatus kind="error" text={t('people.searchFailed')} /> : null}
      <div className="cp-pick-list" role="listbox" aria-multiselectable="true">
        {candidates && !rows.length ? <div className="cp-note">{t('web.channelPanel.noPeople')}</div> : null}
        {rows.map(({ user, name, here }) => {
          const on = selected.includes(user.id);
          const disabled = here || (!on && full);
          return (
            <button key={user.id} className={`list-row clickable cp-pick${on ? ' on' : ''}`} role="option" aria-selected={on} disabled={disabled} onClick={() => toggle(user.id)}>
              <span className="list-row-leading">
                <Avatar userId={user.id} name={name} size={30} version={user.lastPictureUpdate} />
              </span>
              <span className="list-row-text">
                <span className="list-row-title">{name}</span>
                <span className="list-row-subtitle">@{user.username}</span>
              </span>
              <span className="list-row-trailing">{here ? t('web.channelPanel.alreadyMember') : <span className={`cp-check${on ? ' on' : ''}`}>{on ? <Icon name="check" size={13} strokeWidth={2.4} /> : null}</span>}</span>
            </button>
          );
        })}
      </div>
    </Dialog>
  );
}
